import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CredentialVerification = () => {
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const [credentials, setCredentials] = useState([
    {
      id: 1,
      type: 'License',
      name: 'Clinical Psychology License',
      number: 'PSY-12345-CA',
      issuer: 'California Board of Psychology',
      issueDate: '2018-06-15',
      expiryDate: '2026-06-15',
      status: 'verified',
      documentUrl: 'https://example.com/license.pdf'
    },
    {
      id: 2,
      type: 'Certification',
      name: 'Cognitive Behavioral Therapy Certification',
      number: 'CBT-9876',
      issuer: 'American Board of Professional Psychology',
      issueDate: '2020-03-10',
      expiryDate: '2025-03-10',
      status: 'verified',
      documentUrl: 'https://example.com/cert.pdf'
    }
  ]);

  const [newCredential, setNewCredential] = useState({
    type: '',
    name: '',
    number: '',
    issuer: '',
    issueDate: '',
    expiryDate: '',
    document: null
  });

  const credentialTypes = [
    { value: 'license', label: 'Professional License' },
    { value: 'certification', label: 'Certification' },
    { value: 'degree', label: 'Academic Degree' },
    { value: 'training', label: 'Specialized Training' }
  ];

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setNewCredential({ ...newCredential, document: file });
    }
  };

  const handleSubmitCredential = () => {
    if (newCredential?.type && newCredential?.name && newCredential?.number && newCredential?.document) {
      alert('Credential submitted for verification. Our team will review within 2-3 business days.');
      setNewCredential({
        type: '',
        name: '',
        number: '',
        issuer: '',
        issueDate: '',
        expiryDate: '',
        document: null
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-success/10 text-success';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'rejected':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return 'CheckCircle2';
      case 'pending':
        return 'Clock';
      case 'rejected':
        return 'XCircle';
      default:
        return 'AlertCircle';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="Shield" size={20} className="text-accent" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Credential Verification</h2>
          <p className="text-sm text-muted-foreground">Manage your professional credentials and licenses</p>
        </div>
      </div>
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">Verification Process</p>
            <p className="text-sm text-muted-foreground mt-1">
              All professional credentials undergo human review by our compliance team. Verification typically takes 2-3 business days. You'll receive email notification once complete.
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Current Credentials</h3>
          <div className="space-y-3">
            {credentials?.map((credential) => (
              <div key={credential?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="FileText" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{credential?.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {credential?.type} • {credential?.number}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor(credential?.status)}`}>
                    <Icon name={getStatusIcon(credential?.status)} size={14} />
                    {credential?.status?.charAt(0)?.toUpperCase() + credential?.status?.slice(1)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Issuing Authority</p>
                    <p className="text-foreground font-medium mt-1">{credential?.issuer}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Issue Date</p>
                    <p className="text-foreground font-medium mt-1">
                      {new Date(credential.issueDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Expiry Date</p>
                    <p className="text-foreground font-medium mt-1">
                      {new Date(credential.expiryDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Document</p>
                    <Button variant="link" size="sm" iconName="ExternalLink" iconPosition="right" className="mt-1 p-0 h-auto">
                      View Document
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">Add New Credential</h3>
          <div className="bg-muted/50 rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Credential Type"
                placeholder="Select type"
                options={credentialTypes}
                value={newCredential?.type}
                onChange={(value) => setNewCredential({ ...newCredential, type: value })}
                required
              />
              <Input
                label="Credential Name"
                type="text"
                placeholder="e.g., Clinical Psychology License"
                value={newCredential?.name}
                onChange={(e) => setNewCredential({ ...newCredential, name: e?.target?.value })}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="License/Certificate Number"
                type="text"
                placeholder="e.g., PSY-12345-CA"
                value={newCredential?.number}
                onChange={(e) => setNewCredential({ ...newCredential, number: e?.target?.value })}
                required
              />
              <Input
                label="Issuing Authority"
                type="text"
                placeholder="e.g., State Board of Psychology"
                value={newCredential?.issuer}
                onChange={(e) => setNewCredential({ ...newCredential, issuer: e?.target?.value })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Issue Date"
                type="date"
                value={newCredential?.issueDate}
                onChange={(e) => setNewCredential({ ...newCredential, issueDate: e?.target?.value })}
              />
              <Input
                label="Expiry Date"
                type="date"
                value={newCredential?.expiryDate}
                onChange={(e) => setNewCredential({ ...newCredential, expiryDate: e?.target?.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Upload Document <span className="text-destructive">*</span>
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="credential-upload"
                />
                <label htmlFor="credential-upload" className="cursor-pointer">
                  <Icon name="Upload" size={32} className="mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-foreground font-medium">
                    {newCredential?.document ? newCredential?.document?.name : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG up to 10MB</p>
                </label>
              </div>
            </div>

            <Button
              variant="default"
              iconName="Send"
              iconPosition="left"
              onClick={handleSubmitCredential}
              disabled={!newCredential?.type || !newCredential?.name || !newCredential?.number || !newCredential?.document}
            >
              Submit for Verification
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialVerification;